import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../utils/api';

export default function LoginSignupDialog({ open, onClose, onSuccess }) {
  const { login, sendLoginOtp, signupCustomer, verifySignupOtp } = useAuth();
  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtpSent, setForgotOtpSent] = useState(false);
  const [forgotOtp, setForgotOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  const reset = () => {
    setOtp('');
    setOtpSent(false);
    setError('');
  };

  const resetForgot = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotOtpSent(false);
    setForgotOtp('');
    setNewPassword('');
    setForgotSuccess(false);
    setError('');
  };

  useEffect(() => {
    if (!open) {
      resetForgot();
      setName('');
      setEmail('');
      setPhone('');
      setDob('');
      setPassword('');
      setOtp('');
      setOtpSent(false);
      setError('');
      setTab(0);
    }
  }, [open]);

  const handleSendLoginOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    setOtpLoading(true);
    try {
      await sendLoginOtp(email.trim(), password);
      setOtpSent(true);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Enter the OTP sent to your email');
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password, otp.trim());
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob.trim())) {
      setError('Date of birth is required (YYYY-MM-DD)');
      return;
    }
    setLoading(true);
    try {
      await signupCustomer({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        dob: dob.trim(),
        password,
      });
      setOtpSent(true);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignupOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Enter the OTP sent to your email');
      return;
    }
    setLoading(true);
    try {
      await verifySignupOtp(email.trim().toLowerCase(), otp.trim());
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!forgotEmail.trim()) {
      setError('Enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail.trim().toLowerCase());
      setForgotOtpSent(true);
      setForgotOtp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!forgotOtp.trim() || !newPassword) {
      setError('Enter OTP and new password');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setForgotLoading(true);
    try {
      await authApi.resetPassword(forgotEmail.trim().toLowerCase(), forgotOtp.trim(), newPassword);
      setForgotSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Password reset failed');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 0 }}>Log in or Sign up</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => { setTab(v); reset(); resetForgot(); }} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tab label="Login" />
        <Tab label="Sign up" />
      </Tabs>
      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {tab === 0 && showForgotPassword ? (
          forgotSuccess ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>Password reset successfully. You can now log in.</Alert>
              <Button fullWidth variant="outlined" onClick={resetForgot}>Back to Login</Button>
            </Box>
          ) : (
            <form onSubmit={forgotOtpSent ? handleResetPassword : handleForgotSendOtp}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Forgot password</Typography>
              <TextField fullWidth label="Email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} margin="normal" required disabled={forgotOtpSent} />
              {!forgotOtpSent ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Button type="submit" fullWidth variant="contained" disabled={forgotLoading || !forgotEmail.trim()}>
                    {forgotLoading ? <CircularProgress size={24} /> : 'Send OTP'}
                  </Button>
                  <Link component="button" type="button" variant="body2" onClick={resetForgot} sx={{ cursor: 'pointer' }}>
                    Back to Login
                  </Link>
                </Box>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 1 }}>Check your spam folder if you don’t see the email.</Alert>
                  <TextField fullWidth label="OTP" value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} margin="normal" required placeholder="6-digit code" inputProps={{ maxLength: 6 }} />
                  <TextField fullWidth label="New password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} margin="normal" required inputProps={{ minLength: 6 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    <Button type="submit" fullWidth variant="contained" disabled={forgotLoading || !forgotOtp.trim() || !newPassword || newPassword.length < 6}>
                      {forgotLoading ? <CircularProgress size={24} /> : 'Reset password'}
                    </Button>
                    <Link component="button" type="button" variant="body2" onClick={resetForgot} sx={{ cursor: 'pointer' }}>
                      Back to Login
                    </Link>
                  </Box>
                </>
              )}
            </form>
          )
        ) : tab === 0 && (
          <form onSubmit={otpSent ? handleLogin : handleSendLoginOtp}>
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required disabled={otpSent} />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required disabled={otpSent} />
            {!otpSent && (
              <Link component="button" type="button" variant="body2" onClick={() => { setError(''); setShowForgotPassword(true); }} sx={{ display: 'block', mt: 0.5, cursor: 'pointer' }}>
                Forgot password?
              </Link>
            )}
            {!otpSent ? (
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={otpLoading || !email.trim() || !password}>
                {otpLoading ? <CircularProgress size={24} /> : 'Send OTP'}
              </Button>
            ) : (
              <>
                <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} margin="normal" required placeholder="6-digit code" inputProps={{ maxLength: 6 }} />
                <Button type="button" size="small" onClick={reset} sx={{ mt: 0.5 }}>Use different email</Button>
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading || !otp.trim()}>
                  {loading ? <CircularProgress size={24} /> : 'Log in'}
                </Button>
              </>
            )}
          </form>
        )}

        {tab === 1 && (
          <form onSubmit={otpSent ? handleVerifySignupOtp : handleSignup}>
            <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} margin="normal" />
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required disabled={otpSent} />
            <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} margin="normal" disabled={otpSent} />
            <TextField
              fullWidth
              label="Date of birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              margin="normal"
              required
              disabled={otpSent}
              InputLabelProps={{ shrink: true }}
            />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required disabled={otpSent} />
            {!otpSent ? (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                disabled={loading || !email.trim() || !password || !dob.trim()}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign up & Send OTP'}
              </Button>
            ) : (
              <>
                <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} margin="normal" required placeholder="6-digit code" inputProps={{ maxLength: 6 }} />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading || !otp.trim()}>
                  {loading ? <CircularProgress size={24} /> : 'Verify & Continue'}
                </Button>
              </>
            )}
          </form>
        )}
      </Box>
    </Dialog>
  );
}
